
import React, { useEffect, useRef } from 'react';
import { X, Target, Flag, TrendingUp, FileText, Brain, Lightbulb, Layers, Calendar, Scale } from 'lucide-react';
import { Chart, registerables } from 'chart.js';
import { formatValue, perfColor, perfIcon, perfPct, getPerspectiveIcon } from '../utils/formatters';

Chart.register(...registerables);

const DetailModal = ({ selectedIndicator, indicators, closeDetails }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const PerformanceIcon = ({ actual, target, higherIsBetter }) => {
    const iconName = perfIcon(actual, target, higherIsBetter);
    if (iconName === 'trending-up') return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (iconName === 'trending-down') return <TrendingUp className="w-4 h-4 text-red-600 rotate-180" />;
    return <span className="w-4 h-4 text-gray-400">-</span>;
  };
  useEffect(() => {
    console.log('Selected Indicator:', selectedIndicator);
    if (selectedIndicator && chartRef.current) {
      const ctx = chartRef.current.getContext('2d');

      // Destroy existing chart
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      // Get historical data
      const historicalData = indicators
        .filter(ind => ind.indicator === selectedIndicator.indicator && ind.period <= selectedIndicator.period)
        .sort((a, b) => new Date(a.period) - new Date(b.period))
        .slice(-12) // Last 6 periods
        .map(ind => ({
          label: new Date(ind.period).toLocaleDateString('pt-BR', {
            month: 'short',
            year: '2-digit'
          }),
          realizado: ind.actual,
          meta: ind.target
        }));

      // Create new chart
      chartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: historicalData.map(d => d.label),
          datasets: [
            {
              label: 'Realizado',
              data: historicalData.map(d => d.realizado),
              borderColor: 'rgba(34, 197, 94, 1)',
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              borderWidth: 3,
              tension: 0.4,
              fill: true,
              pointBackgroundColor: 'rgba(34, 197, 94, 1)',
              pointBorderColor: 'white',
              pointBorderWidth: 2,
              pointRadius: 5,
            },
            {
              label: 'Meta',
              data: historicalData.map(d => d.meta),
              borderColor: 'rgba(59, 130, 246, 1)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderWidth: 2,
              borderDash: [8, 4],
              tension: 0.4,
              pointBackgroundColor: 'rgba(59, 130, 246, 1)',
              pointBorderColor: 'white',
              pointBorderWidth: 2,
              pointRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                usePointStyle: true,
                padding: 10,
                font: { size: 12, weight: '600' },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
              },
            },
            x: {
              grid: {
                display: false,
              },
            },
          },
        },
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [selectedIndicator, indicators]);

  if (!selectedIndicator) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[100vh] overflow-y-auto modal-slide-in">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Target className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              {selectedIndicator.indicator}
              <div className="text-sm font-normal text-gray-600">{selectedIndicator.perspective}</div>
            </div>
          </h2>

          <button
            onClick={closeDetails}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* KPI Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Flag className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-blue-800">Meta</h3>
              </div>
              <p className="text-2xl font-bold text-blue-900">
                {formatValue(selectedIndicator.target, selectedIndicator.unit)}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <PerformanceIcon
                  actual={selectedIndicator.actual}
                  target={selectedIndicator.target}
                  higherIsBetter={selectedIndicator.higher_is_better}
                />
                <h3 className="font-semibold text-green-800">Realizado</h3>
              </div>
              <p className="text-2xl font-bold text-green-900">
                {formatValue(selectedIndicator.actual, selectedIndicator.unit)}
              </p>
            </div>
            <div className={`p-4 ${perfColor(selectedIndicator.actual, selectedIndicator.target, selectedIndicator.higher_is_better)} rounded-lg border border-green-200`}>
              <div className="flex items-center gap-2 mb-2">
                <PerformanceIcon
                  actual={selectedIndicator.actual}
                  target={selectedIndicator.target}
                  higherIsBetter={selectedIndicator.higher_is_better}
                />
                <h3 className="font-semibold text-green-800">% Meta - Realizado</h3>
              </div>
              <p className="text-2xl font-bold text-green-900">
                <span className={`${perfColor(selectedIndicator.actual, selectedIndicator.target, selectedIndicator.higher_is_better)}`}>
                  {perfPct(selectedIndicator.actual, selectedIndicator.target, selectedIndicator.higher_is_better)}%
                </span>
              </p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Scale className="w-4 h-4 text-amber-600" />
                <h3 className="font-semibold text-amber-800">Peso</h3>
              </div>
              <p className="text-2xl font-bold text-amber-900">
                {selectedIndicator.weight != null ? parseFloat(selectedIndicator.weight) : 1}
              </p>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4 pb-3 border-b border-gray-200">
              Evolução Histórica (Meta vs Realizado)
            </h3>
            <div className="h-60">
              <canvas ref={chartRef}></canvas>
            </div>
          </div>

          {/* Detailed Info */}
          <div className="grid grid-cols-1 gap-4">
            {/*          
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Observações
              </h3>
              <p className="text-blue-900">{selectedIndicator.notes}</p>
            </div>
            */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                <Brain className="w-4 h-4" />
                Análise dos Resultados
              </h3>
              <p className="text-green-900">{selectedIndicator.result_analysis}</p>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                Ações Propostas
              </h3>
              <p className="text-yellow-900">{selectedIndicator.proposed_actions}</p>
            </div>
          </div>

          {/* Footer Info */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              <span>Perspectiva: <strong>{selectedIndicator.perspective}</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>Período: <strong>{new Date(selectedIndicator.period).toLocaleDateString('pt-BR')}</strong></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailModal;
